'use client'

import { useState } from 'react'
import { Check } from 'lucide-react'
import { trackEvent, trackLovedPetsConversion } from '@/lib/analytics'

type Status = 'idle' | 'submitting' | 'success' | 'error'

export default function LovedPetsContactForm() {
  const [name, setName] = useState('')
  const [phone, setPhone] = useState('')
  const [message, setMessage] = useState('')
  const [company, setCompany] = useState('') // honeypot
  const [status, setStatus] = useState<Status>('idle')
  const [error, setError] = useState('')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (status === 'submitting') return
    setStatus('submitting')
    setError('')

    try {
      const res = await fetch('/api/lovedpets/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, phone, message, company }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Something went wrong.')

      trackEvent('lovedpets_cta_clicked', { channel: 'form', variant: 'closing' })
      trackLovedPetsConversion({ channel: 'form', variant: 'closing' })
      setStatus('success')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong.')
      setStatus('error')
    }
  }

  if (status === 'success') {
    return (
      <div className="bg-white rounded-2xl p-8 text-center text-[#002e65] shadow-lg">
        <div className="w-12 h-12 rounded-full bg-[#004da9] text-white flex items-center justify-center mx-auto mb-4">
          <Check className="w-6 h-6" />
        </div>
        <h3 className="text-[20px] font-semibold mb-1.5">Thank you</h3>
        <p className="text-[15px] text-[#002e65]/80">
          Janine will be in touch as soon as she can.
        </p>
      </div>
    )
  }

  const inputClasses =
    'w-full rounded-xl border border-black/10 bg-white px-4 py-3 text-[16px] text-[#002e65] placeholder:text-[#002e65]/45 focus:outline-none focus:ring-2 focus:ring-[#004da9]/40'

  return (
    <form
      onSubmit={handleSubmit}
      className="bg-white rounded-2xl p-6 sm:p-5 shadow-lg text-left"
    >
      <h3 className="text-[18px] font-semibold text-[#002e65] mb-1">
        Prefer we reach out? Leave your details
      </h3>
      <p className="text-[14px] text-[#002e65]/70 mb-4">
        Janine will get back to you — no cost, no obligation.
      </p>

      {/* Honeypot: hidden from real users, catches bots */}
      <div className="absolute -left-[9999px]" aria-hidden="true">
        <label>
          Company
          <input
            tabIndex={-1}
            autoComplete="off"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
          />
        </label>
      </div>

      <div className="flex flex-col gap-3">
        <input
          type="text"
          required
          placeholder="Your name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className={inputClasses}
          autoComplete="name"
        />
        <input
          type="tel"
          required
          placeholder="Phone number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className={inputClasses}
          autoComplete="tel"
        />
        <textarea
          placeholder="Anything you'd like Janine to know (optional)"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          rows={3}
          className={`${inputClasses} resize-none`}
        />
      </div>

      {status === 'error' && (
        <p className="text-[14px] text-red-600 mt-3">{error}</p>
      )}

      <button
        type="submit"
        disabled={status === 'submitting'}
        className="w-full mt-4 inline-flex items-center justify-center bg-[#004da9] hover:bg-[#003d8a] disabled:opacity-60 text-white text-[16px] font-semibold px-6 py-3.5 rounded-full transition-colors"
      >
        {status === 'submitting' ? 'Sending…' : 'Ask Janine to get in touch'}
      </button>
    </form>
  )
}
